import { X, Trophy, CheckCircle } from 'lucide-react';
import { useAppKitAccount } from '@reown/appkit/react';

import { cn } from '@/lib/utils';
import { lamportsToSol } from '@/utils/solana/lamportsToSol';
import type { SeasonResultsWinner } from '@/types/schemas';

interface WinnersListDrawerProps {
  open: boolean;
  onClose: () => void;
  winners: SeasonResultsWinner[];
  seasonNo: number;
}

export function WinnersListDrawer({
  open,
  onClose,
  winners,
  seasonNo,
}: WinnersListDrawerProps) {
  const { address } = useAppKitAccount();

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={cn(
          'relative w-full max-w-4xl max-h-[90vh]',
          'bg-card rounded-xl shadow-2xl overflow-hidden'
        )}
        style={{ backgroundColor: '#011824' }}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 p-6 border-b-2"
          style={{
            borderColor: '#FEBB01',
            background: 'linear-gradient(135deg, rgba(254, 187, 1, 0.2), rgba(254, 187, 1, 0.1))',
          }}
        >
          <button
            onClick={onClose}
            className={cn(
              'absolute top-4 right-4 p-1 rounded-full',
              'text-white/60 hover:text-white',
              'hover:bg-white/10 transition-colors'
            )}
          >
            <X size={20} />
          </button>

          <div className="flex items-center gap-3">
            <Trophy size={32} className="text-[#FEBB01]" />
            <div>
              <h2
                className="text-2xl font-bold"
                style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: '#FEBB01' }}
              >
                SEASON {seasonNo} WINNERS
              </h2>
              <p className="text-white/60 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {winners.length} total winners
              </p>
            </div>
          </div>
        </div>

        {/* Winners List */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="space-y-3">
            {winners.map((winner, index) => {
              const isCurrentUser = winner.userId && address &&
                winner.maskedAddress.toLowerCase().includes(address.toLowerCase().slice(-4));
              const isTopThree = index < 3;

              return (
                <div
                  key={winner.id}
                  className={cn(
                    'p-4 rounded-lg border-2 transition-all',
                    isCurrentUser && 'ring-2 ring-[#50E4F9] ring-offset-2 ring-offset-[#011824]'
                  )}
                  style={{
                    borderColor: isTopThree ? '#FEBB01' : '#50E4F9',
                    backgroundColor: isCurrentUser
                      ? 'rgba(80, 228, 249, 0.15)'
                      : 'rgba(26, 26, 26, 0.8)',
                  }}
                >
                  <div className="flex items-center justify-between">
                    {/* Left: Rank + Address */}
                    <div className="flex items-center gap-4">
                      <div
                        className={cn(
                          'w-12 h-12 rounded-full flex items-center justify-center font-bold',
                          isTopThree
                            ? 'bg-gradient-to-br from-[#FEBB01] to-[#FB8B00]'
                            : 'bg-white/10'
                        )}
                        style={{ fontFamily: 'Hyperspace Race Capsule, cursive' }}
                      >
                        {isTopThree ? (
                          <span className="text-xl">
                            {winner.drawOrder === 1 ? '🥇' : winner.drawOrder === 2 ? '🥈' : '🥉'}
                          </span>
                        ) : (
                          <span className="text-white/70">#{winner.drawOrder}</span>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <div
                            className="font-mono text-sm"
                            style={{ fontFamily: 'Outfit, sans-serif', color: isCurrentUser ? '#50E4F9' : 'white' }}
                          >
                            {winner.maskedAddress}
                          </div>
                          {isCurrentUser && (
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-[#50E4F9] text-black">
                              YOU
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-white/60 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {winner.prizeLabel}
                        </div>
                      </div>
                    </div>

                    {/* Right: Prize + Status */}
                    <div className="text-right">
                      <div className="flex items-center gap-2 justify-end mb-1">
                        <img src="/solana-sol-logo.svg" alt="SOL" width={20} height={20} />
                        <span
                          className="text-xl font-bold"
                          style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: '#FEBB01' }}
                        >
                          {lamportsToSol(BigInt(winner.prizeAmountLamports)).toFixed(4)}
                        </span>
                      </div>
                      {winner.claimed && (
                        <div className="flex items-center gap-1 text-xs text-emerald-400">
                          <CheckCircle size={14} />
                          <span style={{ fontFamily: 'Outfit, sans-serif' }}>Claimed</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
